import { useState, useEffect, useRef } from "react";
import { searchAddresses } from "../../services/geocodingService";
import ManualAddressEntry from "./ManualAddressEntry";

const PropertyAddressAutocomplete = ({
    onAddressSelect,
    initialValue = "",
    placeholder = "Enter property address",
    className = "",
    disabled = false,
}) => {
    const [inputValue, setInputValue] = useState(initialValue);
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const debounceTimer = useRef(null);

    useEffect(() => {
        setInputValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    const isValidPropertyResult = (item) => {
        const display_name = (item.display_name || "").toLowerCase();

        // ❌ Removed allowedStates restriction (now all US states allowed)

        // Reject obvious invalid POIs (parks, schools, gas stations etc.)
        const invalidTypes = ["park", "school", "hospital", "restaurant", "gas_station", "bus_stop"];
        if (
            invalidTypes.some(
                (t) =>
                    (item.type && item.type.toLowerCase() === t) ||
                    (item.category && item.category.toLowerCase() === t) ||
                    (item.class && item.class.toLowerCase() === t) ||
                    display_name.includes(t)
            )
        ) {
            return false;
        }

        // ✅ Accept anything with a house number + road (true residential addresses)
        if (item.address?.house_number && item.address?.road) {
            return true;
        }

        // ✅ Accept administrative areas (states) and localities (cities) for broader searches
        const administrativeTypes = ["state", "administrative", "boundary"];
        const localityTypes = ["city", "town", "village", "locality"];

        if (
            administrativeTypes.some(
                (t) => (item.type && item.type.toLowerCase().includes(t)) || (item.class && item.class.toLowerCase().includes(t))
            ) ||
            localityTypes.some((t) => (item.type && item.type.toLowerCase().includes(t)) || (item.class && item.class.toLowerCase().includes(t)))
        ) {
            return true;
        }

        // Otherwise, fall back to checking if it's a building or residential place
        const validTypes = ["house", "building", "townhouse", "apartment", "residential", "condo"];
        return validTypes.some((t) => (item.type && item.type.toLowerCase().includes(t)) || (item.class && item.class.toLowerCase().includes(t)));
    };

    const searchAddressesWithService = async (query) => {
        try {
            // Use the new geocoding service instead of direct Nominatim call
            const results = await searchAddresses(query, 15);
            return results;
        } catch (error) {
            console.error("Error fetching addresses:", error);
            return [];
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);

        if (!value.trim() || value.length < 2) {
            setSuggestions([]);
            setShowDropdown(false);
            setIsLoading(false);
            return;
        }

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        setIsLoading(true);
        setShowDropdown(true);

        debounceTimer.current = setTimeout(async () => {
            const results = await searchAddressesWithService(value);
            const filteredResults = results.filter(isValidPropertyResult);

            const formattedSuggestions = filteredResults.slice(0, 8).map((item) => ({
                place_id: item.place_id,
                description: item.description,
                lat: parseFloat(item.lat),
                lng: parseFloat(item.lng),
                address: item.address,
                type: item.type || item.category || item.class,
                category: item.category,
                class: item.class,
            }));

            setSuggestions(formattedSuggestions);
            setHasSearched(true);
            setShowDropdown(true);
            setIsLoading(false);
        }, 1000);
    };

    const handleSuggestionSelect = (suggestion) => {
        setInputValue(suggestion.description);
        setShowDropdown(false);
        setSuggestions([]);

        if (onAddressSelect) {
            const addressComponents = [];
            if (suggestion.address) {
                const addr = suggestion.address;
                if (addr.house_number && addr.road) {
                    addressComponents.push({ types: ["street_number"], long_name: addr.house_number, short_name: addr.house_number });
                    addressComponents.push({ types: ["route"], long_name: addr.road, short_name: addr.road });
                }
                if (addr.city || addr.town || addr.village) {
                    addressComponents.push({
                        types: ["locality"],
                        long_name: addr.city || addr.town || addr.village,
                        short_name: addr.city || addr.town || addr.village,
                    });
                }
                if (addr.state) {
                    addressComponents.push({ types: ["administrative_area_level_1"], long_name: addr.state, short_name: addr.state });
                }
                if (addr.postcode) {
                    addressComponents.push({ types: ["postal_code"], long_name: addr.postcode, short_name: addr.postcode });
                }
                if (addr.country) {
                    addressComponents.push({
                        types: ["country"],
                        long_name: addr.country,
                        short_name: addr.country_code?.toUpperCase() || addr.country,
                    });
                }
            }

            onAddressSelect({
                formatted_address: suggestion.description,
                address_components: addressComponents,
                latitude: suggestion.lat,
                longitude: suggestion.lng,
                types: [suggestion.type || "locality"],
            });
        }
    };

    const handleInputFocus = () => {
        if (suggestions.length > 0) {
            setShowDropdown(true);
        }
    };

    const handleManualEntry = () => {
        setShowManualEntry(true);
        setShowDropdown(false);
    };

    const handleManualAddressSubmit = (manualAddress) => {
        setInputValue(manualAddress.formatted_address);
        setShowManualEntry(false);
        setShowDropdown(false);
        setSuggestions([]);
        setHasSearched(false);

        if (onAddressSelect) {
            onAddressSelect(manualAddress);
        }
    };

    return (
        <div className={`tw-relative tw-w-full ${className}`} ref={dropdownRef}>
            <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                placeholder={placeholder}
                disabled={disabled}
                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg tw-bg-white tw-text-black focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-primary"
                autoComplete="off"
            />

            {showDropdown && (
                <div className="tw-absolute tw-top-full tw-left-0 tw-right-0 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-lg tw-z-50 tw-max-h-60 tw-overflow-y-auto">
                    {isLoading ? (
                        <div className="tw-p-3 tw-text-center tw-text-gray-500">
                            <div className="tw-inline-block tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-primary"></div>
                            <span className="tw-ml-2">Searching...</span>
                        </div>
                    ) : suggestions.length > 0 ? (
                        suggestions.map((suggestion) => (
                            <div
                                key={suggestion.place_id}
                                onClick={() => handleSuggestionSelect(suggestion)}
                                className="tw-p-3 tw-cursor-pointer hover:tw-bg-gray-50 tw-border-b tw-border-gray-100 last:tw-border-b-0"
                            >
                                <div className="tw-text-sm tw-text-gray-900">
                                    <span className="tw-font-medium">
                                        {suggestion.address?.house_number && suggestion.address?.road
                                            ? `${suggestion.address.house_number} ${suggestion.address.road}`
                                            : suggestion.address?.road || suggestion.description.split(",")[0]}
                                    </span>
                                    <span className="tw-text-gray-500 tw-ml-2">
                                        {suggestion.address?.city || suggestion.address?.town || suggestion.address?.village || ""}
                                        {suggestion.address?.state ? `, ${suggestion.address.state}` : ""}
                                        {suggestion.address?.postcode ? ` ${suggestion.address.postcode}` : ""}
                                    </span>
                                </div>
                            </div>
                        ))
                    ) : hasSearched && inputValue.trim().length >= 2 ? (
                        <div className="tw-p-3 tw-text-center tw-text-gray-500">
                            <div className="tw-mb-3">
                                <span>No addresses found matching your search.</span>
                            </div>
                            <button
                                onClick={handleManualEntry}
                                className="tw-w-full tw-px-4 tw-py-2 tw-bg-primary hover:tw-bg-primary_hover tw-text-white tw-rounded-lg tw-transition-colors tw-text-sm"
                            >
                                Enter Address Manually
                            </button>
                        </div>
                    ) : (
                        <div className="tw-p-3 tw-text-center tw-text-gray-500">
                            <span>Please try entering a full street address in the United States.</span>
                        </div>
                    )}
                </div>
            )}

            {showManualEntry && (
                <ManualAddressEntry initialQuery={inputValue} onSubmit={handleManualAddressSubmit} onCancel={() => setShowManualEntry(false)} />
            )}
        </div>
    );
};

export default PropertyAddressAutocomplete;
